import React from 'react';
import {
  DefaultSectionT,
  SectionBase,
  SectionList,
  SectionListProps,
  StyleSheet,
  View,
} from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Renderer from 'shared/renderer';
import { VXLNode } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

// TODO: replace with VXL Component
type VXLSectionListSectionSeparatorProps<ItemT, SectionT = DefaultSectionT> = {
  section: SectionBase<ItemT, SectionT>;
  trailingItem?: ItemT;
  trailingSection?: SectionT;
};

export interface SectionedContentListProps<ItemT, SectionT = DefaultSectionT>
  extends Omit<
    SectionListProps<ItemT, SectionT>,
    'SectionSeparatorComponent' | 'ItemSeparatorComponent'
  > {
  [propName: string]: any;
}

export interface SectionedContentScreenSection {
  sectionHeader?: VXLNode;
  sectionFooter?: VXLNode;
  /**
   * the same as `sectionItems` – `data` is the key the underlying react native type requires here.
   */
  data: VXLNode[];
}

export interface SectionedContentScreenModel {
  header?: VXLNode;
  footer?: VXLNode;
  sections: SectionedContentScreenSection[];
}

interface ListHeaderComponentProps {
  model: VXLNode;
  shouldMaskHeaderFromAmplitude?: boolean;
}

export interface SectionedContentScreenProps
  extends Omit<
    SectionedContentListProps<VXLNode, SectionedContentScreenSection>,
    | 'sections'
    | 'renderItem'
    | 'renderSectionHeader'
    | 'renderSectionFooter'
    | 'ListFooterComponent'
    | 'ListHeaderComponent'
    | 'stickySectionHeadersEnabled'
  > {
  [propName: string]: any;
  model: SectionedContentScreenModel;
  maskCellsFromAmplitude?: boolean;
  maskHeaderFromAmplitude?: boolean;
}

function SectionedContentScreenSeparator<ItemT, SectionT>(
  props: VXLSectionListSectionSeparatorProps<ItemT, SectionT>,
): React.ReactElement | null {
  // section header drawn above and below list item, customize:
  // don't draw between section header and content (ie. if item follows don't draw)
  // don't draw after last section (ie. no section follows)
  const { trailingItem, trailingSection } = props;
  if (trailingItem || !trailingSection) {
    return null;
  }

  return <View style={styles.sectionSeparator} />;
}

export function SectionedContentList<ItemT = any, SectionT = DefaultSectionT>(
  props: SectionedContentListProps<ItemT, SectionT>,
) {
  return (
    <SectionList
      {...props}
      SectionSeparatorComponent={SectionedContentScreenSeparator}
      ItemSeparatorComponent={() => (
        <View style={styles.itemSeparatorContainer}>
          <HairLineDivider />
        </View>
      )}
    />
  );
}

const ListHeaderComponent = ({
  model,
  shouldMaskHeaderFromAmplitude,
}: ListHeaderComponentProps) => {
  return shouldMaskHeaderFromAmplitude ? (
    <AmplitudeMask>
      <Renderer toRender={model} />
    </AmplitudeMask>
  ) : (
    <Renderer toRender={model} />
  );
};

export function SectionedContentScreen(props: SectionedContentScreenProps) {
  const { model, maskCellsFromAmplitude, maskHeaderFromAmplitude } = props;

  return (
    <SectionedContentList
      {...props}
      sections={model.sections}
      stickySectionHeadersEnabled={false}
      keyExtractor={(item) => item?.id?.toString()}
      ListHeaderComponent={
        model.header ? (
          <ListHeaderComponent
            model={model.header}
            shouldMaskHeaderFromAmplitude={maskHeaderFromAmplitude}
          />
        ) : null
      }
      renderSectionHeader={({ section }) =>
        maskHeaderFromAmplitude ? (
          <AmplitudeMask>
            <Renderer toRender={section.sectionHeader} />
          </AmplitudeMask>
        ) : (
          <Renderer toRender={section.sectionHeader} />
        )
      }
      renderItem={({ item }) =>
        maskCellsFromAmplitude ? (
          <AmplitudeMask blockStyleMask>
            <View style={styles.amplitudeChildContainer}>
              <Renderer toRender={item} />
            </View>
          </AmplitudeMask>
        ) : (
          <Renderer toRender={item} />
        )
      }
      renderSectionFooter={({ section }) => (
        <Renderer toRender={section.sectionFooter} />
      )}
      ListFooterComponent={
        model.footer ? <Renderer toRender={model.footer} /> : null
      }
    />
  );
}

const styles = StyleSheet.create({
  amplitudeChildContainer: {
    flex: 1,
  },
  itemSeparatorContainer: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  sectionSeparator: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
});

export default SectionedContentScreen;
